import jwt_decode from 'jwt-decode';
import { $host } from '../../http';

const CACHE_KEY =
    'arento.analytics.v2.actorContext';

const FRESH_TTL_MS =
    2 * 60 * 1000;

let memoryCache = null;
let requestState = null;

function getStorage() {
    if (
        typeof window ===
        'undefined'
    ) {
        return null;
    }

    try {
        return window.localStorage;
    } catch (error) {
        return null;
    }
}

function safeParse(value) {
    if (!value) {
        return null;
    }

    try {
        return JSON.parse(value);
    } catch (error) {
        return null;
    }
}

function getAuthFingerprint() {
    const token =
        getStorage()
            ?.getItem('token');

    if (
        !token ||
        token === 'null' ||
        token === 'undefined'
    ) {
        return 'guest';
    }

    try {
        const decoded =
            jwt_decode(token) || {};

        const userId =
            decoded.id ??
            decoded.userId ??
            decoded.user?.id ??
            null;

        const role =
            decoded.role ??
            decoded.user?.role ??
            '';

        if (userId !== null) {
            return (
                `user:${String(userId)}:` +
                String(role)
            );
        }
    } catch (error) {
        // Сам auth token в analytics-cache не сохраняем.
    }

    return 'authenticated';
}

function loadCache() {
    if (memoryCache) {
        return memoryCache;
    }

    const stored =
        safeParse(
            getStorage()
                ?.getItem(
                    CACHE_KEY
                )
        );

    if (
        stored &&
        typeof stored ===
        'object'
    ) {
        memoryCache =
            stored;
    }

    return memoryCache;
}

function saveCache(value) {
    memoryCache =
        value;

    try {
        getStorage()
            ?.setItem(
                CACHE_KEY,
                JSON.stringify(
                    value
                )
            );
    } catch (error) {
        // Memory cache всё равно работает.
    }
}

function cacheMatches(
    cache,
    visitorId,
    authFingerprint
) {
    return Boolean(
        cache &&

        cache.visitorId ===
        visitorId &&

        cache.authFingerprint ===
        authFingerprint &&

        typeof cache
            .actorContextToken ===
        'string' &&

        cache.actorContextToken
            .length > 0
    );
}

function cacheIsFresh(cache) {
    return (
        Number.isFinite(
            cache?.capturedAt
        ) &&

        Date.now() -
        cache.capturedAt <
        FRESH_TTL_MS
    );
}

async function fetchActorContext(
    visitorId,
    authFingerprint
) {
    const { data } =
        await $host.post(
            '/api/analytics/context/v2',
            {
                visitorId
            }
        );

    if (
        !data?.ok ||
        !data?.actorContextToken
    ) {
        throw new Error(
            'Analytics V2: actor context token was not returned'
        );
    }

    const value = {
        visitorId,
        authFingerprint,

        actorContextToken:
        data.actorContextToken,

        actorType:
            data.actorType ||
            null,

        actorReason:
            data.actorReason ||
            null,

        actorRulesVersion:
            data.actorRulesVersion ||
            null,

        isBot:
            Boolean(
                data.isBot
            ),

        deviceType:
            data.deviceType ||
            null,

        capturedAt:
            Date.now()
    };

    saveCache(value);

    return value;
}

export async function getAnalyticsActorContext(
    visitorId,
    {
        forceRefresh = false
    } = {}
) {
    if (!visitorId) {
        throw new Error(
            'Analytics V2: visitorId is required for actor context'
        );
    }

    const authFingerprint =
        getAuthFingerprint();

    const cached =
        loadCache();

    if (
        !forceRefresh &&

        cacheMatches(
            cached,
            visitorId,
            authFingerprint
        ) &&

        cacheIsFresh(
            cached
        )
    ) {
        return cached;
    }

    const requestKey =
        `${visitorId}:${authFingerprint}`;

    if (
        !requestState ||
        requestState.key !==
        requestKey
    ) {
        const promise =
            fetchActorContext(
                visitorId,
                authFingerprint
            )
                .finally(() => {
                    if (
                        requestState?.key ===
                        requestKey
                    ) {
                        requestState =
                            null;
                    }
                });

        requestState = {
            key:
            requestKey,

            promise
        };
    }

    try {
        return await requestState
            .promise;
    } catch (error) {
        /*
         * Backend token специально
         * без exp.
         *
         * Если сеть временно исчезла,
         * старый snapshot можно
         * использовать для queued event.
         */
        const fallback =
            loadCache();

        if (
            cacheMatches(
                fallback,
                visitorId,
                authFingerprint
            )
        ) {
            return fallback;
        }

        throw error;
    }
}

export function invalidateAnalyticsActorContext() {
    memoryCache = null;
    requestState = null;

    try {
        getStorage()
            ?.removeItem(
                CACHE_KEY
            );
    } catch (error) {
        // noop
    }
}